import { motion } from 'framer-motion'
import { useEffect, useState, type ReactNode } from 'react'

type Props = {
  steps: string[] // exactly 3
  badge: string
  kicker: string
}

const STATION_DURATION = 2400 // ms each station stays active

export default function SectorFlowAnimation({ steps, badge, kicker }: Props) {
  const [active, setActive] = useState(0)

  useEffect(() => {
    const id = setInterval(
      () => setActive((a) => (a + 1) % 3),
      STATION_DURATION,
    )
    return () => clearInterval(id)
  }, [])

  return (
    <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage:
            'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />
      {/* breathing halos */}
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -right-24 -top-20 w-72 h-72 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(59,93,255,0.22), transparent 70%)',
        }}
      />
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)',
        }}
      />

      {/* Top header — badge / kicker / live */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/90 backdrop-blur-sm px-2.5 py-1 text-[10px] sm:text-xs text-accent-800">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{badge}</span>
        </span>
        <span className="hidden md:inline text-[10px] sm:text-xs text-ink-500 truncate">
          {kicker}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] sm:text-xs font-semibold text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </span>
      </div>

      {/* Stations row */}
      <div className="absolute inset-x-3 sm:inset-x-8 top-1/2 -translate-y-1/2 sm:-translate-y-[55%]">
        <div className="relative grid grid-cols-3 gap-2 sm:gap-4 items-stretch">
          {/* Connector line behind stations */}
          <div className="absolute top-[42%] left-[16%] right-[16%] h-px pointer-events-none">
            <div
              className="absolute inset-0 opacity-60"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(59,93,255,0.55) 0%, rgba(59,93,255,0.25) 50%, rgba(59,93,255,0.55) 100%)',
              }}
            />
            {/* Traveling glow */}
            <motion.span
              aria-hidden
              animate={{ x: ['-2%', '102%'] }}
              transition={{
                duration: STATION_DURATION / 1000,
                ease: 'easeInOut',
                repeat: Infinity,
              }}
              className="absolute top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-accent-500"
              style={{ boxShadow: '0 0 16px 4px rgba(59,93,255,0.55)' }}
            />
          </div>

          {[0, 1, 2].map((i) => (
            <Station
              key={i}
              index={i}
              icon={ICONS[i]}
              label={steps[i] ?? ''}
              active={active === i}
            />
          ))}
        </div>
      </div>

      {/* Bottom step indicators */}
      <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-8 right-4 sm:right-8">
        <div className="flex items-center justify-between text-[10px] sm:text-xs">
          {steps.map((s, i) => (
            <div
              key={s + i}
              className="flex items-center gap-1.5 max-w-[33%] truncate"
            >
              <motion.span
                animate={{
                  backgroundColor:
                    active === i ? 'rgb(59,93,255)' : 'rgb(213,213,217)',
                  scale: active === i ? [1, 1.4, 1] : 1,
                }}
                transition={{ duration: 0.6, repeat: active === i ? Infinity : 0 }}
                className="w-1.5 h-1.5 rounded-full"
              />
              <span
                className={`font-mono tracking-widest ${
                  active === i
                    ? 'text-ink-950 font-semibold'
                    : 'text-ink-400'
                }`}
              >
                0{i + 1}
              </span>
              <span
                className={`hidden sm:inline truncate ${
                  active === i ? 'text-ink-700' : 'text-ink-400'
                }`}
              >
                · {s}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ───────────── Station component ─────────────

function Station({
  index,
  icon,
  label,
  active,
}: {
  index: number
  icon: (active: boolean) => ReactNode
  label: string
  active: boolean
}) {
  return (
    <motion.div
      animate={{ y: active ? -2 : 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative aspect-[3/4] sm:aspect-[5/6] flex flex-col"
    >
      {/* Card */}
      <motion.div
        animate={{
          borderColor: active
            ? 'rgba(59,93,255,0.35)'
            : 'rgba(14,14,20,0.06)',
          boxShadow: active
            ? '0 30px 60px -22px rgba(59,93,255,0.45), 0 0 0 1px rgba(59,93,255,0.18)'
            : '0 12px 30px -16px rgba(14,14,20,0.12), 0 0 0 1px rgba(14,14,20,0.04)',
          backgroundColor: active
            ? 'rgba(255,255,255,0.95)'
            : 'rgba(255,255,255,0.75)',
        }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex-1 rounded-2xl backdrop-blur-sm overflow-hidden"
      >
        {/* Active corner halo */}
        <motion.span
          aria-hidden
          animate={{ opacity: active ? 0.7 : 0 }}
          transition={{ duration: 0.6 }}
          className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.45), transparent 70%)',
          }}
        />

        {/* Pulsing ring on active */}
        {active && (
          <motion.span
            aria-hidden
            animate={{ scale: [1, 1.4], opacity: [0.45, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
            className="absolute inset-0 rounded-2xl border border-accent-400 pointer-events-none"
          />
        )}

        {/* Inner content */}
        <div className="relative h-full p-3 sm:p-4 flex flex-col">
          {/* Step number */}
          <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] font-semibold text-accent-700">
            0{index + 1}
          </span>

          {/* Icon centered */}
          <div className="flex-1 flex items-center justify-center">
            <motion.div
              animate={{ scale: active ? 1.05 : 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-colors ${
                active
                  ? 'bg-gradient-to-br from-accent-600 to-accent-700 text-white shadow-[0_18px_36px_-14px_rgba(59,93,255,0.7)]'
                  : 'bg-ink-100 text-ink-400'
              }`}
            >
              {icon(active)}
            </motion.div>
          </div>

          {/* Status pill */}
          <div className="flex items-center gap-1.5">
            <motion.span
              animate={{
                backgroundColor: active
                  ? 'rgb(16,185,129)'
                  : 'rgb(213,213,217)',
              }}
              transition={{ duration: 0.4 }}
              className="w-1.5 h-1.5 rounded-full"
            />
            <span
              className={`text-[9px] sm:text-[10px] font-medium uppercase tracking-[0.15em] ${
                active ? 'text-ink-700' : 'text-ink-400'
              }`}
            >
              {active ? 'Active' : 'Idle'}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Label below */}
      <div className="mt-2 sm:mt-3 text-center px-1">
        <p
          className={`text-[11px] sm:text-sm font-semibold tracking-tight transition-colors ${
            active ? 'text-ink-950' : 'text-ink-600'
          }`}
        >
          {label}
        </p>
      </div>
    </motion.div>
  )
}

// ───────────── Animated icons (3 stations) ─────────────

const ICONS: ((active: boolean) => ReactNode)[] = [
  // 01 — Receive (box with arrow descending repeatedly)
  (active) => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="9" width="18" height="12" rx="2" />
      <path d="M3 13h18" />
      <motion.g
        animate={
          active
            ? { y: [-3, 1, -3], opacity: [0.5, 1, 0.5] }
            : { y: 0, opacity: 0.6 }
        }
        transition={{
          duration: 1.4,
          repeat: active ? Infinity : 0,
          ease: 'easeInOut',
        }}
      >
        <path d="M12 3v6" />
        <path d="M9 7l3 2 3-2" />
      </motion.g>
    </svg>
  ),

  // 02 — Process (rotating gear with pulsing core)
  (active) => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <motion.g
        animate={active ? { rotate: 360 } : { rotate: 0 }}
        transition={{
          duration: 6,
          repeat: active ? Infinity : 0,
          ease: 'linear',
        }}
        style={{ transformOrigin: '12px 12px' }}
      >
        <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.5 6.5l1.4 1.4M16.1 16.1l1.4 1.4M6.5 17.5l1.4-1.4M16.1 7.9l1.4-1.4" />
        <circle cx="12" cy="12" r="5" />
      </motion.g>
      <motion.circle
        cx="12"
        cy="12"
        r="2"
        fill="currentColor"
        stroke="none"
        animate={
          active
            ? { scale: [0.8, 1.15, 0.8], opacity: [0.6, 1, 0.6] }
            : { scale: 1, opacity: 0.7 }
        }
        transition={{
          duration: 1.4,
          repeat: active ? Infinity : 0,
          ease: 'easeInOut',
        }}
        style={{ transformOrigin: '12px 12px' }}
      />
    </svg>
  ),

  // 03 — Ship (truck with bobbing motion + speed lines)
  (active) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <motion.g
        animate={active ? { x: [-1, 1.5, -1] } : { x: 0 }}
        transition={{
          duration: 1.2,
          repeat: active ? Infinity : 0,
          ease: 'easeInOut',
        }}
      >
        <rect x="2" y="9" width="11" height="7" rx="1" />
        <path d="M13 11h4l3 3v2h-7" />
        <circle cx="6" cy="18" r="1.6" />
        <circle cx="17" cy="18" r="1.6" />
      </motion.g>
      <motion.g
        animate={
          active ? { opacity: [0, 0.8, 0], x: [-4, 0, -4] } : { opacity: 0 }
        }
        transition={{
          duration: 1.2,
          repeat: active ? Infinity : 0,
          ease: 'easeOut',
        }}
      >
        <path d="M2 6h3M2 12h2M2 18h2" />
      </motion.g>
    </svg>
  ),
]
